import * as React from "react";
import axios from "axios";

import {
  MyBagPresentationItem,
  MyBagPresentationItem_StockStatus,
} from "../model";

import {
  useMyBagAnalytics,
  useMyBagPresentationModel,
  useMyBagUserExperience_asController,
} from "../providers";

import {
  BbntWishlistItemInput,
  gqlAddProductsToWishlist, GraphqlResponseAddProductsToWishlist,
} from "../../Common";

export const MyBagItems: React.FunctionComponent = () => {
  const { presentationMyBagItems } = useMyBagPresentationModel();
  const { analytics } = useMyBagAnalytics();
  const {
    handleRemoveItem: MyBagUserExperience_handleRemoveItem,
    handleChangeItemQuantity: MyBagUserExperience_handleChangeItemQuantity,
  } = useMyBagUserExperience_asController();

  const handleItemQuantityDecrement: MyBagItems_ProductMutableQuantityProps["handleItemQuantityDecrement"] =
    React.useCallback(
      (presentationMyBagItem) => {
        MyBagUserExperience_handleChangeItemQuantity(
          presentationMyBagItem,
          presentationMyBagItem.howManyOfThisProduct - 1
        );
      },
      [MyBagUserExperience_handleChangeItemQuantity]
    );
  const handleItemQuantityIncrement: MyBagItems_ProductMutableQuantityProps["handleItemQuantityIncrement"] =
    React.useCallback(
      (presentationMyBagItem) => {
        MyBagUserExperience_handleChangeItemQuantity(
          presentationMyBagItem,
          presentationMyBagItem.howManyOfThisProduct + 1
        );
      },
      [MyBagUserExperience_handleChangeItemQuantity]
    );
  const handleItemRemove: MyBagItems_ProductProps["handleItemRemove"] =
    React.useCallback(
      (presentationMyBagItem) => {
        // the whole quote line goes (a gift-card row stands for its line)
        analytics.removeFromCart({
          productId: presentationMyBagItem.productId,
          quantity: presentationMyBagItem.lineQuantity,
        });
        MyBagUserExperience_handleRemoveItem(presentationMyBagItem);

        let eventGA4 = {
          event: "remove_from_cart",
          ecommerce: {
            currency: "AUD",
            value: presentationMyBagItem.subtotal,
            items: [{
              item_id: presentationMyBagItem.sku,
              item_name: presentationMyBagItem.title
            }]
          }
        };

        window.dataLayer.push(eventGA4);
      },
      [MyBagUserExperience_handleRemoveItem, analytics]
    );

  // Triple Whale view_cart: the quote lines (a gift-card line once, whatever its rows),
  // re-evaluated whenever a line's quantity changes, not only the line count.
  const cartLines = React.useMemo(() => {
    const byLine = new Map<string, MyBagPresentationItem>();
    presentationMyBagItems.forEach((item) => {
      if (!byLine.has(item.id)) {
        byLine.set(item.id, item);
      }
    });
    return Array.from(byLine.values());
  }, [presentationMyBagItems]);
  const cartSignature = cartLines
    .map((line) => `${line.productId}:${line.lineQuantity}`)
    .join("|");
  React.useEffect(() => {
    if (cartLines.length === 0) {
      return;
    }
    analytics.viewCart({
      items: cartLines.map((line) => ({
        productId: line.productId,
        quantity: line.lineQuantity,
      })),
      value:
        Math.round(
          cartLines.reduce((sum, line) => sum + line.lineSubtotal, 0) * 100
        ) / 100,
    });
  }, [cartSignature, analytics]);

  React.useEffect(() => {
    if (presentationMyBagItems?.[0]?.sku) {
      setTimeout(() => {
        const event = {
          event: "view_cart",
          ecommerce: {
            currency: "AUD",
            value: parseFloat(document.querySelector('#bbnt-new-checkout-2022--mybag > section > section.details > section.my-bag-summary > div.by-the-numbers > div.totalValueBreakdown > div.total-inc-gst > p.the-price.add-price > span.value')?.innerText),
            items: presentationMyBagItems.map((item) => ({
              item_id: item.sku,
              item_name: item.title,
              price: item.subtotal,
              quantity: item.howManyOfThisProduct
            }))
          }
        };

        window.dataLayer?.push(event);
      }, 2000);
    }
  }, [presentationMyBagItems.length]);

  return (
    <ul>
      {presentationMyBagItems.map((presentationMyBagItem) => {
        return (
          <li key={presentationMyBagItem.id}>
            <MyBagItems_Product
              presentationMyBagItem={presentationMyBagItem}
              handleItemQuantityDecrement={handleItemQuantityDecrement}
              handleItemQuantityIncrement={handleItemQuantityIncrement}
              handleItemRemove={handleItemRemove}
            />
          </li>
        );
      })}
    </ul>
  );
};

interface MyBagItems_ProductProps {
  presentationMyBagItem: MyBagPresentationItem;

  handleItemQuantityDecrement: MyBagItems_ProductMutableQuantityProps["handleItemQuantityDecrement"];
  handleItemQuantityIncrement: MyBagItems_ProductMutableQuantityProps["handleItemQuantityIncrement"];

  handleItemRemove: (product: MyBagPresentationItem) => void;
}
const MyBagItems_Product: React.FunctionComponent<MyBagItems_ProductProps> = (
  props
) => {
    // const [isLoading, setIsLoading] = React.useState(false);
  const title = React.useMemo(() => {
    return (
      <a className="title" href={`${props.presentationMyBagItem.url}`}>
        {props.presentationMyBagItem.title}
      </a>
    );
  }, [props.presentationMyBagItem.title, props.presentationMyBagItem.url]);
  const productImage = React.useMemo(() => {
    return (
      <img
        src={props.presentationMyBagItem.imageThumbnail}
        alt={props.presentationMyBagItem.title}
      />
    );
  }, [
    props.presentationMyBagItem.title,
    props.presentationMyBagItem.imageThumbnail,
  ]);
  const handleItemRemove: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(() => {
      props.handleItemRemove(props.presentationMyBagItem);
    }, [props.presentationMyBagItem, props.handleItemRemove]);

  const handleMoveToWishlist: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(async () => {
      // setIsLoading(true);

      const product = props.presentationMyBagItem;
      const newWishlistItemInput: BbntWishlistItemInput = {
        parent_sku: 'G_EGYPTINDUL_080101_00390',
        sku: product.sku,
        productId: '49',
        quantity: product.howManyOfThisProduct,
      };

      const gqlQuery = gqlAddProductsToWishlist(
          [newWishlistItemInput]
      );

      const headers = {
        "Content-Type": "application/json",
        Store: 'default',
      };

      const { data } = await axios.post<GraphqlResponseAddProductsToWishlist>(
          "/graphql",
          { query: gqlQuery },
          { headers }
      );

      const { data: gqlResponseData } = data || {};
      const { addProductsToWishlist } = gqlResponseData || {};

      const areThereItems = addProductsToWishlist.wishlist && addProductsToWishlist.wishlist.items && (addProductsToWishlist.wishlist.items?.length || 0) > 0;

      // setIsLoading(false);

      if (!areThereItems) {
        throw new Error("No items were added to the wishlist");
      } else {
        props.handleItemRemove(props.presentationMyBagItem);
      }
    }, [props.presentationMyBagItem]);

  const removeItemControl = React.useMemo(() => {
    return (
      <button className="remove-item-button" onClick={handleItemRemove}>
        <span>{`Remove`}</span>
      </button>
    );
  }, [handleItemRemove]);

  const moveToWishlist = React.useMemo(() => {
    return (
      <button className="move-to-wishlist" onClick={handleMoveToWishlist}>
        <span>{`Move to Wishlist`}</span>
      </button>
    );
  }, [handleMoveToWishlist]);

  const memberPricing = React.useMemo(() => {
    if (!props.presentationMyBagItem.isMemberPrice) {
      return null;
    }
    return (
      <React.Fragment>
        <p className="rewards-member-discount-applied">{`Rewards member discount applied`}</p>
      </React.Fragment>
    );
  }, [props.presentationMyBagItem.isMemberPrice]);

  const stockStatus = React.useMemo(() => {
    if (props.presentationMyBagItem.isUnavailable) {
      const messages = props.presentationMyBagItem.serverErrors;
      return messages.length > 0 ? (
        <React.Fragment>
          {messages.map((message, index) => (
            <p key={index} className="stock-status out-of-stock">{message}</p>
          ))}
        </React.Fragment>
      ) : (
        <p className="stock-status out-of-stock">{`Out of stock`}</p>
      );
    }
    switch (props.presentationMyBagItem.currentStockStatus) {
      case MyBagPresentationItem_StockStatus.IN_STOCK:
        return null;
      case MyBagPresentationItem_StockStatus.LOW_STOCK:
        return (
          <p className="stock-status hurry-low-stock">{`Hurry, limited stock left!`}</p>
        );
      default:
        return null;
    }
  }, [
    props.presentationMyBagItem.currentStockStatus,
    props.presentationMyBagItem.isUnavailable,
    props.presentationMyBagItem.serverErrors,
  ]);

  const asAGiftcard = React.useMemo(() => {
    if (!props.presentationMyBagItem.asAGiftcard?.sendTo) {
      return null;
    }
    return (
      <React.Fragment>
        <p className="giftcard-sends-to-rich-label">
          <span className="static-label">{`Send to:`}&nbsp;</span>
          <span className="value">{`${props.presentationMyBagItem.asAGiftcard.sendTo}`}</span>
        </p>
      </React.Fragment>
    );
  }, [props.presentationMyBagItem]);

  const mobileWhatIsTheItem = React.useMemo(() => {
    return (
      <div className="mobile-only what-is-the-item">
        <a className="left" href={`${props.presentationMyBagItem.url}`}>
          {productImage}
        </a>
        <div className="right">
          {title}
          <MyBagItems_ProductSku value={props.presentationMyBagItem.sku} />
          {asAGiftcard}
          {!asAGiftcard ? (
            <MyBagItems_ProductItemPrice value={props.presentationMyBagItem} />
          ) : null}
        </div>
      </div>
    );
  }, [props.presentationMyBagItem, productImage, title, asAGiftcard]);
  const mobileWhatCanIDoWithTheItem = React.useMemo(() => {
    const isThisAGiftcard = !!props.presentationMyBagItem.asAGiftcard?.sendTo;
    return (
      <div
        className={`mobile-only what-can-i-do-with-the-item ${
          isThisAGiftcard
            ? `btw-this-is-a-giftcard`
            : `btw-this-is-not-a-giftcard`
        }`}
      >
        <div className="left">{removeItemControl}</div>
        <div className="right">
          <MyBagItems_ProductMutableQuantity
            presentationMyBagItem={props.presentationMyBagItem}
            handleItemQuantityDecrement={props.handleItemQuantityDecrement}
            handleItemQuantityIncrement={props.handleItemQuantityIncrement}
          />
          <MyBagItems_ProductSubtotal value={props.presentationMyBagItem} />
        </div>
      </div>
    );
  }, [
    props.presentationMyBagItem,
    props.handleItemQuantityDecrement,
    props.handleItemQuantityIncrement,
    productImage,
    removeItemControl,
    props.presentationMyBagItem.asAGiftcard?.sendTo,
  ]);
  const mobile = React.useMemo(() => {
    return (
      <React.Fragment>
        {mobileWhatIsTheItem}
        {mobileWhatCanIDoWithTheItem}
      </React.Fragment>
    );
  }, [mobileWhatIsTheItem, mobileWhatCanIDoWithTheItem]);
  const desktop = React.useMemo(() => {
    return (
      <React.Fragment>
        <a
          className="desktop-only first-column"
          href={`${props.presentationMyBagItem.url}`}
        >
          {productImage}
        </a>
        <div className="desktop-only second-column">
          {title}
          <MyBagItems_ProductSku value={props.presentationMyBagItem.sku} />
          {asAGiftcard}
          {!asAGiftcard ? (
            <MyBagItems_ProductItemPrice value={props.presentationMyBagItem} />
          ) : null}
          <div className="button-container">
            {removeItemControl}
            {/*{moveToWishlist}*/}
          </div>
        </div>
        <div className="desktop-only third-column">
          <MyBagItems_ProductMutableQuantity
            presentationMyBagItem={props.presentationMyBagItem}
            handleItemQuantityDecrement={props.handleItemQuantityDecrement}
            handleItemQuantityIncrement={props.handleItemQuantityIncrement}
          />
          <MyBagItems_ProductSubtotal value={props.presentationMyBagItem} />
        </div>
        <div className="desktop-only fourth-column"></div>
      </React.Fragment>
    );
  }, [
    props.presentationMyBagItem,
    props.handleItemQuantityDecrement,
    props.handleItemQuantityIncrement,
    productImage,
    title,
    asAGiftcard,
    removeItemControl,
  ]);
  return (
    <div className="bbnt-checkout-2022-mybag-item">
      {mobile}
      {desktop}
      <div className="any-callouts">
        {memberPricing}
        {stockStatus}
      </div>
    </div>
  );
};

interface MyBagItems_ProductMutableQuantityProps {
  presentationMyBagItem: MyBagPresentationItem;
  handleItemQuantityDecrement: (
    presentationMyBagItem: MyBagPresentationItem
  ) => void;
  handleItemQuantityIncrement: (
    presentationMyBagItem: MyBagPresentationItem
  ) => void;
}
const MyBagItems_ProductMutableQuantity: React.FunctionComponent<
  MyBagItems_ProductMutableQuantityProps
> = (props) => {
  const handleItemQuantityDecrement: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(() => {
      props.handleItemQuantityDecrement(props.presentationMyBagItem);
    }, [props.presentationMyBagItem, props.handleItemQuantityDecrement]);
  const handleItemQuantityIncrement: React.MouseEventHandler<HTMLButtonElement> =
    React.useCallback(() => {
      props.handleItemQuantityIncrement(props.presentationMyBagItem);
    }, [props.presentationMyBagItem, props.handleItemQuantityIncrement]);
  return (
    <div className="product-mutable-quantity-control">
      {!!props.presentationMyBagItem.asAGiftcard?.sendTo ? null : (
        <React.Fragment>
          <button
            className="decrement"
            onClick={handleItemQuantityDecrement}
          ></button>
          <p>{`${props.presentationMyBagItem.howManyOfThisProduct}`}</p>
          <button
            className="increment"
            onClick={handleItemQuantityIncrement}
          ></button>
        </React.Fragment>
      )}
    </div>
  );
};

interface MyBagItems_ProductSkuProps {
  value: string;
}
const MyBagItems_ProductSku: React.FunctionComponent<
  MyBagItems_ProductSkuProps
> = (props) => {
  return (
    <p className="sku">
      <span className="item-code-label">{`Item code: `}</span>
      <span className="item-code-sku">{`${props.value}`}</span>
    </p>
  );
};

interface MyBagItems_ProductItemPriceProps {
  value: MyBagPresentationItem;
}
const MyBagItems_ProductItemPrice: React.FunctionComponent<
  MyBagItems_ProductItemPriceProps
> = (props) => {
  return (
    <div
      className={`item-price ${
        props.value.isMemberPrice
          ? "member-pricing"
          : props.value.isSalePrice
          ? "sale-pricing"
          : "regular-pricing"
      }`}
    >
      {(props.value.isMemberPrice || props.value.isSalePrice) && (
        <p
          className={`the-price-afterwards 
            ${
              props.value.isMemberPrice
                ? "the-member-price"
                : props.value.isSalePrice
                ? "the-sale-price"
                : "never"
            }
          `}
        >
          <span className="dollar-sign">{`\$`}</span>
          <span className="actual-value">
            {`${props.value.itemPrice.toFixed(2)}`}
          </span>
        </p>
      )}
      <p
        className={`the-regular-price ${
          props.value.isMemberPrice || props.value.isSalePrice
            ? "the-normally-price"
            : ""
        }`}
      >
        <span className="dollar-sign">{`\$`}</span>
        <span className="actual-value">
          {`${
            props.value.isMemberPrice || props.value.isSalePrice
              ? props.value.itemPriceNormally.toFixed(2)
              : props.value.itemPrice.toFixed(2)
          }`}
        </span>
      </p>
    </div>
  );
};

interface MyBagItems_ProductSubtotalProps {
  value: MyBagPresentationItem;
}
const MyBagItems_ProductSubtotal: React.FunctionComponent<
  MyBagItems_ProductSubtotalProps
> = (props) => {
  return (
    <div
      className={`item-subtotal ${
        props.value.isMemberPrice
          ? "member-pricing"
          : props.value.isSalePrice
          ? "sale-pricing"
          : "regular-pricing"
      }`}
    >
      <p className="preamble">
        <span className="subtotal-preamble">{`Subtotal`}</span>
        <span className="subtotal-preamble-dollar-sign-space">&nbsp;</span>
      </p>

      {(props.value.isMemberPrice || props.value.isSalePrice) && (
        <p
          className={`the-price-afterwards 
            ${
              props.value.isMemberPrice
                ? "the-member-price"
                : props.value.isSalePrice
                ? "the-sale-price"
                : "never"
            }
          `}
        >
          <span className="dollar-sign">{`\$`}</span>
          <span className="actual-value">
            {`${props.value.subtotal.toFixed(2)}`}
          </span>
        </p>
      )}
      <p
        className={`the-regular-price ${
          props.value.isMemberPrice || props.value.isSalePrice
            ? "the-normally-price"
            : ""
        }`}
      >
        <span className="dollar-sign">{`\$`}</span>
        <span className="actual-value">
          {`${
            props.value.isMemberPrice || props.value.isSalePrice
              ? props.value.subtotalNormally.toFixed(2)
              : props.value.subtotal.toFixed(2)
          }`}
        </span>
      </p>
    </div>
  );
};
